import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Textarea,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Badge,
  Tooltip,
  Container,
  Progress,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { ArrowBackIcon, CopyIcon, DownloadIcon, CheckIcon, ViewIcon } from '@chakra-ui/icons';

const BATCH_SIZE = 25;
const MAX_URLS = 500;

function SEOScraper() {
  const navigate = useNavigate();
  const [urls, setUrls] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [copiedTable, setCopiedTable] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [copiedSource, setCopiedSource] = useState(false);
  const { isOpen: isSourceOpen, onOpen: onSourceOpen, onClose: onSourceClose } = useDisclosure();

  const handleViewSource = (result) => {
    setSelectedSource(result);
    setCopiedSource(false);
    onSourceOpen();
  };

  const handleCopySource = () => {
    if (!selectedSource?.bodyHtml) return;
    navigator.clipboard.writeText(selectedSource.bodyHtml).then(() => {
      setCopiedSource(true);
      setTimeout(() => setCopiedSource(false), 2000);
    });
  };

  const handleScrape = async () => {
    // Keep empty lines to preserve row alignment
    const urlList = urls.split('\n').map((url) => url.trim());

    // Check if there's at least one non-empty URL
    const hasValidUrls = urlList.some((url) => url.length > 0);
    if (!hasValidUrls) {
      setError('Please enter at least one URL');
      return;
    }

    if (urlList.length > MAX_URLS) {
      setError(`Maximum ${MAX_URLS} URLs allowed. You entered ${urlList.length}.`);
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setProgress(0);
    setProgressText('Starting...');

    const allResults = [];
    const totalUrls = urlList.length;
    const totalBatches = Math.ceil(totalUrls / BATCH_SIZE);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';

      for (let i = 0; i < totalBatches; i++) {
        const start = i * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, totalUrls);
        const batchUrls = urlList.slice(start, end);

        setProgressText(`Processing batch ${i + 1} of ${totalBatches} (URLs ${start + 1}-${end} of ${totalUrls})`);

        const response = await fetch(`${apiUrl}/scrape-seo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ urls: batchUrls }),
        });

        const data = await response.json();

        if (data.success) {
          allResults.push(...data.results);
          const completedUrls = Math.min((i + 1) * BATCH_SIZE, totalUrls);
          setProgress(Math.round((completedUrls / totalUrls) * 100));
        } else {
          // If a batch fails, add error entries for those URLs
          batchUrls.forEach((url) => {
            allResults.push({
              url,
              title: null,
              slug: null,
              metaTitle: null,
              metaDescription: null,
              h1: null,
              error: data.error || 'Batch request failed',
            });
          });
        }
      }

      setResults(allResults);
      setProgressText('Complete!');
    } catch (err) {
      console.error('Scrape error:', err);
      setError('Unable to connect to the server. Please try again.');
    } finally {
      setLoading(false);
      setProgress(100);
    }
  };

  const generateCSV = () => {
    if (!results || results.length === 0) return '';

    const headers = ['URL', 'Slug', 'Meta Title', 'Meta Description', 'H1', 'Source Code', 'Status'];
    const rows = results.map((r) => {
      // Empty row for alignment
      if (r.isEmpty) {
        return ['', '', '', '', '', '', ''];
      }
      return [
        r.url,
        r.slug || '',
        `"${(r.metaTitle || '').replace(/"/g, '""')}"`,
        `"${(r.metaDescription || '').replace(/"/g, '""')}"`,
        `"${(r.h1 || '').replace(/"/g, '""')}"`,
        `"${(r.bodyHtml || '').replace(/"/g, '""')}"`,
        r.error ? 'Error' : 'Success',
      ];
    });

    return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
  };

  const handleExportCSV = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    link.href = URL.createObjectURL(blob);
    link.download = `seo-data-${timestamp}.csv`;
    link.click();
  };

  const generateTableText = () => {
    if (!results || results.length === 0) return '';

    const headers = ['URL', 'Slug', 'Meta Title', 'Meta Description', 'H1', 'Source Code'];
    const rows = results.map((r) => {
      // Empty row for alignment
      if (r.isEmpty) {
        return ['', '', '', '', '', ''];
      }
      // Collapse tabs and newlines in source HTML so it stays on a single TSV cell
      const sourceCell = (r.bodyHtml || '').replace(/[\t\r\n]+/g, ' ');
      return [
        r.url,
        r.slug || '',
        r.metaTitle || '',
        r.metaDescription || '',
        r.h1 || '',
        sourceCell,
      ];
    });

    return [headers.join('\t'), ...rows.map((row) => row.join('\t'))].join('\n');
  };

  const handleCopyTable = () => {
    const tableText = generateTableText();
    navigator.clipboard.writeText(tableText).then(() => {
      setCopiedTable(true);
      setTimeout(() => setCopiedTable(false), 2000);
    });
  };

  const handleReset = () => {
    setResults(null);
    setError(null);
    setProgress(0);
    setProgressText('');
  };

  const truncate = (str, len = 50) => {
    if (!str) return '-';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  // Count total lines (including empty for alignment)
  const urlCount = urls.trim() ? urls.split('\n').length : 0;

  return (
    <Box minH="100vh" bg="#0F1419">
      <Container maxW="1400px" py={8}>
        {/* Navigation */}
        <HStack mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={() => (results ? handleReset() : navigate('/'))}
            variant="ghost"
            colorScheme="purple"
            size="sm"
            color="gray.400"
            _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
          >
            {results ? 'New Scrape' : 'Back to Tools'}
          </Button>
        </HStack>

        {/* Header */}
        <VStack spacing={8} align="stretch">
          <Box textAlign="center" mb={4}>
            <Heading as="h1" fontSize="4xl" fontWeight="bold" color="white" mb={2}>
              SEO Meta Scraper
            </Heading>
            <Text fontSize="md" color="gray.400" fontWeight="medium">
              Bulk extract SEO metadata from up to {MAX_URLS} URLs
            </Text>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              status="error"
              borderRadius="md"
              bg="red.900"
              borderColor="red.700"
              borderWidth="1px"
            >
              <AlertIcon />
              <AlertDescription color="white">{error}</AlertDescription>
            </Alert>
          )}

          {/* Input Form */}
          {!results && !loading && (
            <Box
              bg="#1A1F2E"
              p={10}
              borderRadius="lg"
              boxShadow="2xl"
              borderWidth="1px"
              borderColor="gray.700"
              maxW="800px"
              mx="auto"
              w="100%"
            >
              <VStack spacing={6}>
                <Box w="100%">
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="semibold" color="white">
                      Enter URLs (one per line)
                    </Text>
                    <Badge
                      colorScheme={urlCount > MAX_URLS ? 'red' : urlCount > 0 ? 'green' : 'gray'}
                      fontSize="xs"
                    >
                      {urlCount} / {MAX_URLS} URLs
                    </Badge>
                  </HStack>
                  <Textarea
                    value={urls}
                    onChange={(e) => setUrls(e.target.value)}
                    placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
                    bg="#0F1419"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                    _hover={{ borderColor: 'gray.500' }}
                    _focus={{
                      borderColor: 'pink.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-pink-500)',
                    }}
                    fontSize="sm"
                    fontFamily="mono"
                    rows={12}
                    resize="vertical"
                  />
                  <Text color="gray.500" fontSize="xs" mt={2}>
                    Enter full URLs including https:// - Maximum {MAX_URLS} URLs per scrape
                  </Text>
                </Box>

                <Button
                  onClick={handleScrape}
                  size="lg"
                  width="full"
                  isDisabled={!urls.trim() || urlCount > MAX_URLS}
                  bg="pink.500"
                  color="white"
                  _hover={{
                    bg: 'pink.600',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                  fontWeight="semibold"
                >
                  Scrape {urlCount > 0 ? `${urlCount} URL${urlCount !== 1 ? 's' : ''}` : 'URLs'}
                </Button>
              </VStack>
            </Box>
          )}

          {/* Loading State with Progress */}
          {loading && (
            <Box
              bg="#1A1F2E"
              p={10}
              borderRadius="lg"
              boxShadow="2xl"
              borderWidth="1px"
              borderColor="gray.700"
              maxW="600px"
              mx="auto"
              w="100%"
            >
              <VStack spacing={6}>
                <Spinner
                  thickness="3px"
                  speed="0.65s"
                  emptyColor="gray.700"
                  color="pink.500"
                  size="xl"
                />
                <VStack spacing={2}>
                  <Text color="white" fontSize="lg" fontWeight="semibold">
                    Scraping URLs...
                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    {progressText}
                  </Text>
                </VStack>
                <Box w="100%">
                  <HStack justify="space-between" mb={2}>
                    <Text color="gray.500" fontSize="xs">
                      Progress
                    </Text>
                    <Text color="pink.400" fontSize="sm" fontWeight="bold">
                      {progress}%
                    </Text>
                  </HStack>
                  <Progress
                    value={progress}
                    colorScheme="pink"
                    size="lg"
                    borderRadius="md"
                    bg="gray.700"
                    hasStripe
                    isAnimated
                  />
                </Box>
                <Text color="gray.500" fontSize="xs" textAlign="center">
                  Processing in batches of {BATCH_SIZE} URLs for optimal performance
                </Text>
              </VStack>
            </Box>
          )}

          {/* Results Table */}
          {results && (
            <Box
              bg="#1A1F2E"
              p={6}
              borderRadius="lg"
              boxShadow="2xl"
              borderWidth="1px"
              borderColor="gray.700"
            >
              <VStack spacing={6} align="stretch">
                {/* Results Header */}
                <HStack justify="space-between" flexWrap="wrap" gap={4}>
                  <HStack>
                    <Heading size="md" color="white">
                      Results
                    </Heading>
                    <Badge colorScheme="green" fontSize="sm">
                      {results.filter((r) => !r.error && !r.isEmpty).length} / {results.filter((r) => !r.isEmpty).length} successful
                    </Badge>
                  </HStack>
                  <HStack>
                    <Button
                      leftIcon={copiedTable ? <CheckIcon /> : <CopyIcon />}
                      onClick={handleCopyTable}
                      size="sm"
                      colorScheme={copiedTable ? 'green' : 'purple'}
                      variant="outline"
                    >
                      {copiedTable ? 'Copied!' : 'Copy Table'}
                    </Button>
                    <Button
                      leftIcon={<DownloadIcon />}
                      onClick={handleExportCSV}
                      size="sm"
                      colorScheme="pink"
                    >
                      Export CSV
                    </Button>
                  </HStack>
                </HStack>

                {/* Table */}
                <TableContainer maxH="600px" overflowY="auto">
                  <Table variant="simple" size="sm">
                    <Thead position="sticky" top={0} bg="#1A1F2E" zIndex={1}>
                      <Tr>
                        <Th color="gray.400" borderColor="gray.600">
                          #
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          URL
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          Slug
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          Meta Title
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          Meta Description
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          H1
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          Source Code
                        </Th>
                        <Th color="gray.400" borderColor="gray.600">
                          Status
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {results.map((result, index) => (
                        <Tr key={index} bg={result.isEmpty ? 'whiteAlpha.50' : 'transparent'}>
                          <Td borderColor="gray.700" color="gray.500" fontSize="xs">
                            {index + 1}
                          </Td>
                          <Td borderColor="gray.700" maxW="200px">
                            {result.isEmpty ? (
                              <Text color="gray.600" fontSize="xs">-</Text>
                            ) : (
                              <Tooltip label={result.url} placement="top">
                                <Text
                                  color="blue.300"
                                  fontSize="xs"
                                  fontFamily="mono"
                                  isTruncated
                                >
                                  {truncate(result.url, 30)}
                                </Text>
                              </Tooltip>
                            )}
                          </Td>
                          <Td borderColor="gray.700">
                            <Text color="gray.300" fontSize="xs" fontFamily="mono">
                              {result.isEmpty ? '-' : (result.slug || '-')}
                            </Text>
                          </Td>
                          <Td borderColor="gray.700" maxW="150px">
                            {result.isEmpty ? (
                              <Text color="gray.600" fontSize="xs">-</Text>
                            ) : (
                              <Tooltip label={result.metaTitle || 'N/A'} placement="top">
                                <Text color="white" fontSize="xs" isTruncated>
                                  {truncate(result.metaTitle, 30)}
                                </Text>
                              </Tooltip>
                            )}
                          </Td>
                          <Td borderColor="gray.700" maxW="200px">
                            {result.isEmpty ? (
                              <Text color="gray.600" fontSize="xs">-</Text>
                            ) : (
                              <Tooltip label={result.metaDescription || 'N/A'} placement="top">
                                <Text color="gray.300" fontSize="xs" isTruncated>
                                  {truncate(result.metaDescription, 40)}
                                </Text>
                              </Tooltip>
                            )}
                          </Td>
                          <Td borderColor="gray.700" maxW="150px">
                            {result.isEmpty ? (
                              <Text color="gray.600" fontSize="xs">-</Text>
                            ) : (
                              <Tooltip label={result.h1 || 'N/A'} placement="top">
                                <Text color="white" fontSize="xs" isTruncated>
                                  {truncate(result.h1, 30)}
                                </Text>
                              </Tooltip>
                            )}
                          </Td>
                          <Td borderColor="gray.700" textAlign="center">
                            {result.isEmpty || !result.bodyHtml ? (
                              <Text color="gray.600" fontSize="xs">-</Text>
                            ) : (
                              <Tooltip label="View source code" placement="top">
                                <IconButton
                                  icon={<ViewIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="purple"
                                  color="purple.300"
                                  aria-label="View source code"
                                  onClick={() => handleViewSource(result)}
                                />
                              </Tooltip>
                            )}
                          </Td>
                          <Td borderColor="gray.700">
                            {result.isEmpty ? (
                              <Text color="gray.600" fontSize="xs">-</Text>
                            ) : (
                              <Badge
                                colorScheme={result.error ? 'red' : 'green'}
                                fontSize="xs"
                              >
                                {result.error ? 'Error' : 'OK'}
                              </Badge>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>

      {/* Source Code Modal */}
      <Modal isOpen={isSourceOpen} onClose={onSourceClose} size="4xl" scrollBehavior="inside" isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#1A1F2E" color="white" borderWidth="1px" borderColor="gray.700" maxH="85vh">
          <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
            <Text fontSize="md" fontWeight="semibold">Source Code</Text>
            {selectedSource?.url && (
              <Text fontSize="xs" color="gray.400" fontFamily="mono" mt={1} isTruncated>
                {selectedSource.url}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody py={4}>
            <Box
              bg="#0F1419"
              p={4}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.700"
              maxH="60vh"
              overflow="auto"
            >
              <Text
                as="pre"
                fontFamily="mono"
                fontSize="xs"
                color="gray.300"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                m={0}
              >
                {selectedSource?.bodyHtml || 'No source available.'}
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor="gray.700">
            <Button
              leftIcon={copiedSource ? <CheckIcon /> : <CopyIcon />}
              onClick={handleCopySource}
              size="sm"
              colorScheme={copiedSource ? 'green' : 'purple'}
              variant="outline"
              mr={3}
              isDisabled={!selectedSource?.bodyHtml}
            >
              {copiedSource ? 'Copied!' : 'Copy HTML'}
            </Button>
            <Button onClick={onSourceClose} size="sm" variant="ghost" color="gray.400">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SEOScraper;
